import React, { useCallback, useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import ZionForm, { useFieldArray, useFormState, useWatch, useZionForm } from '@fs/zion-form'
import {
  useSlsDispatchContext,
  useSlsStateContext,
  generateSls,
  saveSlsAndUpdateState,
  getNextArkId,
  useSlsHighlightsContext,
  useGenderTranslations,
  getRecordTypesFromSurroundingRecords,
  getImage,
} from '@fs/zion-sls'
import { useFeatureFlags } from '@fs/zion-flags'
import { getUser } from '@fs/zion-user'
import { ContentAdd } from '@fs/zion-icon'
import { Alert, Button, Divider, FlowGrid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { buildEssentialInformationDispatches } from '../utils/buildEssentialInformationDispatches'
import { errorCollector } from '../../../../../shared/utils/utils'
import { useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import NamePartsFormSection from './NamePartsFormSection'
import PersonalDetailsFormSection from './PersonalDetailsFormSection'
import { getDefaultNameTemplate } from '../utils/helpers'
import { headers } from '../../../../../shared/services/config'
import { prepareEssentialInformationDefaultValues } from '../utils/essentialInformationParsing'
import { essentialInformationValidationSchema } from '../utils/validationSchema'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { getFieldIdsFromFormValues } from '../../shared/utils/highlightUtils'
import CardFooter from '../../shared/components/CardFooter'
import { useDisplayHighlights } from '../../../../hooks/useDisplayHighlights'
import { MAKE_NEW_RECORD } from '../../familyMembersCard/utils/saveRelationships'

// This is the maximum number of additional names that can be added, NOT counting the primary name
const MAX_ALTERNATIVE_NAMES = 2

/**
 * EssentialInformationForm component renders a section for entering and managing essential information
 * about a person, including handling for adding/removing alternative names and personal details.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.person - The person object containing personal details to be displayed or edited.
 * @param {Function} props.saveCardDataToSls - Function to save the card data to SLS.
 * @param {Function} props.onSave - Callback function invoked after successful save with dispatches, sls, and formValues.
 * @param {boolean} [props.reviewed=false] - Whether the card has been reviewed (showing Next button).
 * @param {string} props.appName - The application name for tracking.
 * @param {boolean} props.isPrimaryPerson - Whether this person is the primary person for the record.
 * @param {Function} props.setReviewed - Callback to mark the form as reviewed.
 * @param {Function} props.onDirtyChange - Callback invoked when the form's dirty state changes.
 * @param {Function} props.setIsEditing - Callback to update the editing state.
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @returns {JSX.Element} The rendered EssentialInformationForm component.
 */
const EssentialInformationForm = ({
  person,
  saveCardDataToSls,
  onSave,
  reviewed = false,
  appName,
  isPrimaryPerson,
  setReviewed,
  onDirtyChange,
  setIsEditing,
  splitScreenPartitionKey,
}) => {
  const [t] = useTranslation()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [userId, setUserId] = useState(null)
  const { currentImageArk, images, types } = useSlsStateContext()
  const slsDispatch = useSlsDispatchContext()
  const { topMostPanel } = useOverlayContext()
  const { getDispatches, commitEdits, clearEdits } = useSlsHighlightsContext()
  const r2Dispatch = useR2DispatchContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const genderTranslations = useGenderTranslations()
  const currentImage = getImage(images, currentImageArk)
  // Image can be empty while loading after a navigation, so we check for its existence
  const surroundingRecordTypes = currentImage
    ? getRecordTypesFromSurroundingRecords(currentImage)?.sort((a, b) => a.count - b.count)
    : []
  const forcePrimaryPerson = surroundingRecordTypes?.[0]?.type === 'CENSUS'

  // Fetch ARK ID for new persons eagerly on mount when person.id is not available
  const [personId, setPersonId] = useState(person?.id || null)
  const fetchNewPersonId = useCallback(async () => {
    try {
      const id = await getNextArkId('person')
      setPersonId(id)
      r2Dispatch({ type: 'SET_SELECTED_PERSON_ID', value: id })
      return id
    } catch (error) {
      console.error('Failed to fetch new ARK ID for person:', error)
      return null
    }
  }, [r2Dispatch])

  // Eagerly fetch personId on mount for new persons (when person?.id is null/undefined/blank)
  useEffect(() => {
    if (!person?.id && !personId) {
      fetchNewPersonId()
    }
  }, [person?.id, personId, fetchNewPersonId])

  const defaultValues = useMemo(
    () =>
      prepareEssentialInformationDefaultValues(
        person,
        types,
        t,
        genderTranslations,
        images,
        isPrimaryPerson,
        forcePrimaryPerson
      ),
    [person, types, t, genderTranslations, isPrimaryPerson, forcePrimaryPerson, images]
  )

  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(essentialInformationValidationSchema, { context: { defaultValues } }),
  })

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  const fieldArrayMethods = useFieldArray({
    name: 'names',
    keyName: 'formId', // To avoid conflict with 'id' from our data
    control: formMethods.control,
  })

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  const { fields, append } = fieldArrayMethods

  const handleSubmit = useCallback(
    async (currentValues) => {
      setSaving(true)
      let finalPersonId = defaultValues.id
      let finalRecordId = person?.recordId

      // For new persons (those with temporary placeholder IDs), generate a real ARK ID during submission
      if (!person?.id) {
        try {
          finalPersonId = personId || (await getNextArkId('person'))
          // Update the current values with the real ARK ID
          currentValues = { ...currentValues, id: finalPersonId }
        } catch (error) {
          setSaving(false)
          // eslint-disable-next-line no-console
          console.warn('Failed to get ARK ID, using temporary placeholder ID:', error)
        }
      }

      // Generate recordId if person doesn't have one (new person without record)
      if (!person?.recordId || person?.recordId === MAKE_NEW_RECORD) {
        finalRecordId = await getNextArkId('record')
        currentValues = { ...currentValues, recordId: finalRecordId }
      }

      try {
        const uniqueDispatches = []
        const dispatchSet = new Set()
        const pushUnique = (d) => {
          if (!d) return
          const key = JSON.stringify(d)
          if (dispatchSet.has(key)) return
          dispatchSet.add(key)
          uniqueDispatches.push(d)
        }

        setSaveError(null)

        // Build dispatches using the buildEssentialInformationDispatches function
        const { dispatches: essentialInfoDispatches } = buildEssentialInformationDispatches({
          originalValues: defaultValues,
          currentValues,
          partitionKey: currentImageArk,
          personId: finalPersonId,
          appName,
          imagesState: images,
          forcePrimaryPerson,
        })

        // Get highlight dispatches from the highlights context
        const highlightDispatches = getDispatches({
          mainImageArk: currentImageArk,
          splitScreenImageArk: splitScreenPartitionKey,
        })

        essentialInfoDispatches.forEach(pushUnique)
        highlightDispatches.forEach(pushUnique)

        // Combine all dispatches
        const dispatches = uniqueDispatches

        // Generate SLS actions from dispatches
        const sls = generateSls(dispatches)

        if (saveCardDataToSls && sls?.length > 0) {
          // Save to SLS and update state
          await saveSlsAndUpdateState({
            sls,
            dispatches,
            saveChangesFlag,
            slsActionsSplunkDebugger,
            userId: userId || person?.id,
            dispatch: slsDispatch,
            appName,
            fsUserAgentChain: headers['FS-User-Agent-Chain'],
            successHandler: () => {
              // Processing SLS dispatches to update local state even if the saveChangesFlag is off
              if (!saveChangesFlag.isOn) {
                slsDispatch({
                  type: 'BATCH_DISPATCHES',
                  value: dispatches,
                })
              }

              if (onSave) {
                onSave({ dispatches, sls, formValues: currentValues, personId: finalPersonId })
              }
              commitEdits()
              setSaving(false)
            },
            errorHandler: (error) => {
              setSaving(false)
              setSaveError(error)
              errorCollector(error, null, {
                codeId: 'ESSENTIAL_INFO_SAVE_ERROR',
                sls,
                dispatches,
                status: error?.response?.status,
                responseBody: error?.response?.data,
              })
            },
            saveInitiatorFilename: 'EssentialInformationCard.js',
          })
        } else if (onSave) {
          onSave({ dispatches, sls, formValues: currentValues, personId: finalPersonId })
          setSaving(false)

          // Processing SLS dispatches to update local state even if the saveChangesFlag is off
          if (!saveChangesFlag.isOn) {
            slsDispatch({
              type: 'BATCH_DISPATCHES',
              value: dispatches,
            })
          }
          commitEdits()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('💥 FORM SUBMIT: Unexpected error in handleSubmit', {
          error,
          currentValues,
          defaultValues,
          currentImageArk,
          personId: person?.id,
          timestamp: new Date().toISOString(),
        })
        setSaving(false)
        setSaveError(error)
        errorCollector(error, null, {
          codeId: 'ESSENTIAL_INFO_BUILD_DISPATCHES_ERROR',
        })
      }
    },
    [
      defaultValues,
      person?.id,
      person?.recordId,
      currentImageArk,
      appName,
      images,
      saveCardDataToSls,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      userId,
      slsDispatch,
      onSave,
      getDispatches,
      commitEdits,
      forcePrimaryPerson,
      splitScreenPartitionKey,
      personId,
    ]
  )

  // Display highlights for all fields in edit mode
  const fieldIds = useMemo(() => getFieldIdsFromFormValues(watchedFormValues), [watchedFormValues])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: !['editSharedHighlightPage1', 'editSharedHighlightPage2', 'highlightsPanel'].includes(topMostPanelId),
  })

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    getUser().then((u) => setUserId(u.cisId))
  }, [])

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
    setReviewed(true)
    clearEdits()
  }, [formMethods, setIsEditing, setReviewed, clearEdits, onDirtyChange])

  return (
    <ZionForm methods={formMethods} onSubmit={handleSubmit}>
      <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
        {fields.map((field, index) => (
          <NamePartsFormSection
            key={field.id}
            field={field}
            index={index}
            fieldArrayMethods={fieldArrayMethods}
            person={person}
            splitScreenImageArk={splitScreenPartitionKey}
          />
        ))}

        <Button
          disabled={fields.filter((field) => !field?.removed).length >= MAX_ALTERNATIVE_NAMES + 1}
          Icon={ContentAdd}
          emphasis="medium"
          onClick={() => append(getDefaultNameTemplate(person))}
          data-testid="add-name-button"
          linkName="essential-information-add-name"
        >
          {t('essentialInformationForm.additionalName', 'Additional Name')}
        </Button>

        <Divider />

        <PersonalDetailsFormSection
          person={person}
          personId={personId}
          forcePrimaryPerson={forcePrimaryPerson}
          splitScreenImageArk={splitScreenPartitionKey}
        />

        {saveError && (
          <Alert emphasis="high" type="error" color="red">
            {t('FormValidation.saveError', 'There was an error saving your changes. Please try again.')}
          </Alert>
        )}
        <CardFooter reviewed={reviewed} onCancel={handleCancel} saving={saving} label="essential-information" />
      </FlowGrid>
    </ZionForm>
  )
}

EssentialInformationForm.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string,
    primary: PropTypes.bool,
  }).isRequired,
  saveCardDataToSls: PropTypes.bool.isRequired,
  onSave: PropTypes.func,
  reviewed: PropTypes.bool,
  appName: PropTypes.string.isRequired,
  isPrimaryPerson: PropTypes.bool.isRequired,
  setReviewed: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  splitScreenPartitionKey: PropTypes.string,
}

export default EssentialInformationForm
